import React, { useState, useEffect } from "react";
import { Table, Button, Space, Input, Row, Col, Modal, Form, message, Upload } from "antd";
import { EditOutlined, DeleteOutlined, PlusOutlined, UploadOutlined } from "@ant-design/icons";
import api, { API_BASE_URL } from "../api";

const Visitor = () => {
    const [data, setData] = useState([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [currentRecord, setCurrentRecord] = useState(null);
    const [form] = Form.useForm();
    const [searchText, setSearchText] = useState("");
    const [filteredData, setFilteredData] = useState([]);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const res = await api.get("/visitor/get-all-visitors");
            if (res.data && res.data.success) {
                setData(res.data.data);
                setFilteredData(res.data.data);
            }
        } catch (err) {
            console.error("Error fetching visitors:", err);
            message.error("Failed to fetch visitors.");
        }
    };

    const handleSearch = (value) => {
        setSearchText(value);
        const filtered = data.filter(item =>
            item.name.toLowerCase().includes(value.toLowerCase()) ||
            item.phone.includes(value)
        );
        setFilteredData(filtered);
    };

    const handleAdd = () => {
        setEditMode(false);
        setCurrentRecord(null);
        form.resetFields();
        setIsModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditMode(true);
        setCurrentRecord(record);
        form.setFieldsValue({
            ...record,
            photo: record.photo ? [{ uid: '-1', name: 'photo.jpg', status: 'done', url: `${API_BASE_URL}/${record.photo}` }] : []
        });
        setIsModalVisible(true);
    };

    const handleDelete = (id) => {
        Modal.confirm({
            title: "Are you sure you want to delete this visitor?",
            onOk: async () => {
                try {
                    await api.delete(`/visitor/deleteVisitor/${id}`);
                    message.success("Visitor deleted successfully");
                    fetchData();
                } catch (err) {
                    message.error("Failed to delete visitor.");
                }
            },
        });
    };

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            const formData = new FormData();
            formData.append("name", values.name);
            formData.append("phone", values.phone);
            formData.append("address", values.address || "");
            formData.append("amount", values.amount || 0);

            if (values.photo && values.photo[0] && values.photo[0].originFileObj) {
                formData.append("photo", values.photo[0].originFileObj);
            }

            if (editMode) {
                await api.put(`/visitor/updateVisitor/${currentRecord._id}`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                message.success("Visitor updated successfully");
            } else {
                await api.post("/visitor/addVisitor", formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                message.success("Visitor added successfully");
            }
            setIsModalVisible(false);
            fetchData();
        } catch (err) {
            console.error("Form validation failed:", err);
        }
    };

    const columns = [
        { title: "S.No", render: (_, __, index) => index + 1 },
        {
            title: "Photo",
            dataIndex: "photo",
            key: "photo",
            render: (photo) => (
                <img
                    src={photo ? `${API_BASE_URL}/${photo}` : "https://via.placeholder.com/50"}
                    alt="Visitor"
                    style={{ width: 50, height: 50, borderRadius: "4px", objectFit: "cover" }}
                />
            ),
        },
        { title: "Name", dataIndex: "name", key: "name" },
        { title: "Phone", dataIndex: "phone", key: "phone" },
        { title: "Address", dataIndex: "address", key: "address" },
        { title: "Amount", dataIndex: "amount", key: "amount" },
        {
            title: "Actions",
            key: "actions",
            render: (_, record) => (
                <Space>
                    <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} />
                    <Button icon={<DeleteOutlined />} onClick={() => handleDelete(record._id)} />
                </Space>
            ),
        },
    ];

    return (
        <div style={{ padding: "20px" }}>
            <h2 style={{ color: "teal", marginBottom: "20px" }}>Visitor List</h2>
            <Row gutter={16} style={{ marginBottom: "20px" }}>
                <Col span={12}>
                    <Input.Search
                        placeholder="Search by name or phone"
                        allowClear
                        onSearch={handleSearch}
                        onChange={(e) => handleSearch(e.target.value)}
                    />
                </Col>
                <Col span={12} style={{ textAlign: "right" }}>
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd} style={{ backgroundColor: "teal" }}>
                        Add Visitor
                    </Button>
                </Col>
            </Row>
            <Table columns={columns} dataSource={filteredData} rowKey="_id" />

            <Modal
                title={editMode ? "Edit Visitor" : "Add Visitor"}
                visible={isModalVisible}
                onOk={handleOk}
                onCancel={() => setIsModalVisible(false)}
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="name" label="Name" rules={[{ required: true, message: "Please enter name" }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="phone" label="Phone Number" rules={[{ required: true, message: "Please enter phone number" }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="address" label="Address">
                        <Input.TextArea />
                    </Form.Item>
                    <Form.Item name="amount" label="Amount">
                        <Input type="number" />
                    </Form.Item>
                    <Form.Item
                        name="photo"
                        label="Photo"
                        valuePropName="fileList"
                        getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
                    >
                        <Upload listType="picture-card" maxCount={1} beforeUpload={() => false}>
                            <Button icon={<UploadOutlined />}>Upload</Button>
                        </Upload>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default Visitor;
